import React, { useState } from 'react';
import { processMobileDeposit } from '../../utils/firestore';
import { useAuth } from '../../contexts/AuthContext';

export default function MobileDeposit({ accounts }) {
  const { currentUser } = useAuth();
  const [selectedAccount, setSelectedAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    if (!selectedAccount || !amount || !frontImage || !backImage) {
      console.error('Please fill in all required fields');
      setLoading(false);
      return;
    }

    const depositAmount = parseFloat(amount);
    if (isNaN(depositAmount) || depositAmount <= 0) {
      console.error('Please enter a valid amount');
      setLoading(false);
      return;
    }

    if (depositAmount > 10000) {
      console.error('Mobile deposits are limited to $10,000 per check');
      setLoading(false);
      return;
    }

    try {
      await processMobileDeposit({
        toAccount: selectedAccount,
        amount: depositAmount,
        frontImage,
        backImage,
        userId: currentUser.uid
      });

      setSuccess(true);
      setLoading(false);
      setSelectedAccount('');
      setAmount('');
      setFrontImage(null);
      setBackImage(null);
    } catch (error) {
      console.error('Deposit failed. Please try again.', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const inputClass = 'w-full p-3 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 bg-gray-50 dark:!bg-slate-700 text-gray-900 dark:text-slate-100 hover:bg-white dark:hover:!bg-slate-600 transition-colors duration-200 placeholder-gray-400 dark:placeholder-slate-400';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2';

  const ImageUploadBox = ({ label, image, setImage, id }) => (
    <div className="relative">
      <input
        type="file"
        id={id}
        className="hidden"
        accept="image/*"
        onChange={(e) => setImage(e.target.files[0])}
        disabled={loading}
      />
      <label
        htmlFor={id}
        className={`block w-full aspect-video border-2 border-dashed rounded-xl 
          ${image ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 dark:border-emerald-600' : 'border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700'} 
          hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors duration-200 cursor-pointer`}
      >
        <div className="flex flex-col items-center justify-center h-full p-6">
          {image ? (
            <>
              <span className="text-3xl mb-2">✓</span>
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Image uploaded</span>
              <span className="text-xs text-gray-500 dark:text-slate-400 mt-1">{image.name}</span>
            </>
          ) : (
            <>
              <span className="text-3xl mb-2">📸</span>
              <span className="text-sm font-medium text-gray-700 dark:text-slate-200">{label}</span>
              <span className="text-xs text-gray-500 dark:text-slate-400 mt-1">Click to upload</span>
            </>
          )}
        </div>
      </label>
    </div>
  );

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Check deposit</h3>
      
      {success && (
        <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-200 rounded-xl flex items-center">
          <span className="text-xl mr-2">✓</span>
          Check deposit submitted. Funds will be available according to our deposit policy.
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className={labelClass}>
            Pay into
          </label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className={inputClass}
            required
            disabled={loading}
          >
            <option value="">Select account</option>
            {accounts.map((account) => (
              <option key={account.type} value={account.type}>
                {account.type} (*{account.accountNumber.slice(-4)}) - ${account.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>
            Amount
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-slate-400">$</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`${inputClass} pl-8`}
              placeholder="0.00"
              min="0.01"
              step="0.01"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageUploadBox
            label="Front of check"
            image={frontImage}
            setImage={setFrontImage}
            id="front-image"
          />
          <ImageUploadBox
            label="Back of check"
            image={backImage}
            setImage={setBackImage}
            id="back-image"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !amount || !frontImage || !backImage}
          className={`w-full py-3 px-4 rounded-xl text-white font-medium
            ${loading || !amount || !frontImage || !backImage
              ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-primary-500'
            } transition-colors duration-200`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Processing…
            </div>
          ) : (
            'Deposit check'
          )}
        </button>

        <div className="mt-6 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-transparent dark:border-slate-600">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Important</h3>
          <ul className="text-sm text-gray-600 dark:text-slate-300 space-y-2">
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Check must be signed and endorsed on the back
            </li>
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Write “For mobile deposit only” below your signature on the back
            </li>
            <li className="flex items-center">
              <span className="text-emerald-500 mr-2">•</span>
              Photos must be clear and well lit
            </li>
          </ul>
        </div>
      </form>
    </div>
  );
} 